import type { ForwardedRef } from "react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";

import { handleSuggestionMenuKeyDown } from "./handleSuggestionMenuKeyDown";
import type { SuggestionMenuHandle } from "./types";

export interface SuggestionListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface UseSuggestionListKeyboardOptions {
  handleKeyDown?: (
    event: KeyboardEvent,
    dismiss: () => void,
  ) => boolean | undefined;
  isEnabled: boolean;
  onDismiss?: () => void;
  query: string;
  ref: ForwardedRef<SuggestionListHandle>;
}

export const useSuggestionListKeyboard = ({
  handleKeyDown,
  isEnabled,
  onDismiss,
  query,
  ref,
}: UseSuggestionListKeyboardOptions) => {
  const menuRef = useRef<SuggestionMenuHandle | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(false);
  }, [query]);

  const isOpen = !isDismissed && isEnabled;

  const dismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      const menu = menuRef.current;
      if (!menu || !isOpen) return false;

      const wasCustomHandled = handleKeyDown?.(event, dismiss);
      const wasHandled =
        wasCustomHandled !== undefined
          ? wasCustomHandled
          : handleSuggestionMenuKeyDown({
              key: event.key,
              menu,
              onDismiss: dismiss,
            });

      if (wasHandled) {
        event.preventDefault();
        event.stopPropagation();
      }

      return wasHandled;
    },
  }));

  return { isOpen, menuRef };
};
